import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Hls from 'hls.js';
import Modal from './Modal';
import { getAssetPlaybackUrl } from '../data/uploadService';
import { createStillGrabAsset, getAssetDoc } from '../data/firestore';
import { httpsify } from '../utils/url';
import { fmtTime } from '../utils/movie';
import { gradeSignature, DEFAULT_GRADE, PRESETS, applyLook, RED_DEFAULT_GRADE, autoExposureStops } from '../utils/grade';
import GradedCanvas from './GradedCanvas';
import CropBox from './CropBox';

const IDENTITY = gradeSignature(DEFAULT_GRADE);

// Aspect presets for reframing the still. `r` is output width/height. `orig`
// keeps the whole frame (no crop); `free` allows any-shape corner cropping; the
// rest lock the crop box to that ratio while you drag a corner.
const ASPECTS = [
  { id: 'orig', label: 'Original', r: null },
  { id: 'free', label: 'Free', r: null },
  { id: '3:2', label: '3:2', r: 3 / 2 },
  { id: '16:9', label: '16:9', r: 16 / 9 },
  { id: '4:5', label: '4:5', r: 4 / 5 },
  { id: '1:1', label: '1:1', r: 1 },
  { id: '9:16', label: '9:16', r: 9 / 16 },
];

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Given a target output aspect, a 0..1 scale and a normalized center, compute
// the normalized crop rect {x,y,w,h} that stays inside the source frame.
// `srcAR` is the source frame's width/height.
function rectFor(targetAR, scale, center, srcAR) {
  if (!targetAR || !srcAR) return null;
  const r = targetAR / srcAR; // = normW / normH
  let maxW;
  let maxH;
  if (r >= 1) {
    maxW = 1;
    maxH = 1 / r;
  } else {
    maxH = 1;
    maxW = r;
  }
  const w = clamp(scale, 0.1, 1) * maxW;
  const h = clamp(scale, 0.1, 1) * maxH;
  const x = clamp(center.x - w / 2, 0, 1 - w);
  const y = clamp(center.y - h / 2, 0, 1 - h);
  return { x, y, w, h };
}

// Scrub a clip (RED proxy or MP4) and save a full-resolution still as a trip
// PHOTO. The backend `stills` worker decodes the chosen frame from the original
// max-res source, bakes the clip's saved color/light look and any crop, and
// drops the photo into the trip's media (where it can fill a magazine spot).
export default function FrameGrabber({ asset, tripId, onClose, onSaved, gradeOverride, startTime = 0, movieId = '', movieTitle = '' }) {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const [videoEl, setVideoEl] = useState(null);
  const [url, setUrl] = useState('');
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);
  const [srcAR, setSrcAR] = useState(0);
  const isRed = !!asset?.isRawVideo;
  // Scrub the lightweight proxy in the preview (fast frame seeking) but render
  // the still from the full-quality master original. Two proxy shapes exist for
  // non-RED clips: an in-place HLS proxy (asset.proxyHlsUrl) on the master, or a
  // separate 1080p proxy *asset* (this card is the proxy, or the master points
  // at one via vproxy.proxyAssetId).
  const vproxy = asset?.vproxy || {};
  const separateProxyId = !isRed && !asset?.isProxy ? vproxy.proxyAssetId : null;

  // Render source = the full-quality original. When this card is a proxy, climb
  // to its master; otherwise the asset on screen already IS the original.
  const [renderSource, setRenderSource] = useState(asset);
  useEffect(() => {
    let on = true;
    if (asset?.isProxy && asset?.proxyOf && tripId) {
      getAssetDoc(tripId, asset.proxyOf)
        .then((m) => on && m && setRenderSource(m))
        .catch(() => {});
    } else {
      setRenderSource(asset);
    }
    return () => { on = false; };
  }, [asset, tripId]);

  // When the master has a *separate* proxy asset, resolve it so we can preview
  // its (small) file instead of the heavy original.
  const [proxyDoc, setProxyDoc] = useState(null);
  useEffect(() => {
    let on = true;
    if (separateProxyId && tripId) {
      getAssetDoc(tripId, separateProxyId)
        .then((p) => on && p && setProxyDoc(p))
        .catch(() => {});
    } else {
      setProxyDoc(null);
    }
    return () => { on = false; };
  }, [separateProxyId, tripId]);

  // Are we previewing a proxy while rendering the full original? (Drives the
  // "grabs the master, not this proxy" hint.)
  const previewingProxy = !isRed && (!!asset?.isProxy || !!asset?.proxyHlsUrl || !!proxyDoc);

  // Source timeline fps — used for exact 1-frame stepping and the frame readout.
  const fps =
    Number(
      renderSource?.srcFps ||
        renderSource?.fpsAvg ||
        renderSource?.metadata?.video?.fpsAvg ||
        asset?.proxyFps ||
        asset?.srcFps ||
        asset?.fpsAvg ||
        asset?.metadata?.video?.fpsAvg ||
        0
    ) || 30;
  const [playing, setPlaying] = useState(false);
  const frameNo = Math.round((t || 0) * fps);
  const totalFrames = dur ? Math.round(dur * fps) : 0;
  // Every grab is saved as a non-destructive PNG negative now; `format` is kept
  // only for the createStillGrabAsset call signature (it's ignored downstream).
  const [format] = useState('png');
  // RED only: target an exact native frame index (the proxy clock can't address
  // it because the native fps differs). Empty → use the scrubbed time.
  const [exactFrame, setExactFrame] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [name, setName] = useState('');
  const [tags, setTags] = useState('');

  // RED auto-exposure. Differently-exposed Log3G10 clips de-log to wildly
  // different brightness (a flat/under-exposed clip crushes to a dark dusk look),
  // so each clip is normalized to a consistent IPP2 baseline. The authoritative
  // value is `redMeta.autoExposure` (stops), computed per-clip on the backend
  // from clean decoded frames — stable and free of canvas timing/taint issues.
  // A live measurement is only a fallback for clips decoded before that existed.
  const storedAutoExp = (() => {
    const v = renderSource?.redMeta?.autoExposure ?? asset?.redMeta?.autoExposure;
    return Number.isFinite(v) ? v : null;
  })();
  const [exposure, setExposure] = useState(storedAutoExp ?? 0);
  const [autoExp, setAutoExp] = useState(storedAutoExp ?? 0);
  const [expTouched, setExpTouched] = useState(false);
  const expTouchedRef = useRef(false);
  const sampleCanvasRef = useRef(null);

  // Adopt the per-clip backend value as soon as it resolves (redMeta can arrive
  // a tick after the master doc), unless the user has already dialed it in.
  useEffect(() => {
    if (storedAutoExp == null) return;
    setAutoExp(storedAutoExp);
    if (!expTouchedRef.current) setExposure(storedAutoExp);
  }, [storedAutoExp]);

  const measureExposure = () => {
    if (!isRed || storedAutoExp != null) return; // backend value wins
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    let c = sampleCanvasRef.current;
    if (!c) { c = document.createElement('canvas'); sampleCanvasRef.current = c; }
    const w = 80;
    const h = Math.max(2, Math.round((80 * v.videoHeight) / v.videoWidth));
    c.width = w; c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    let data;
    try {
      ctx.drawImage(v, 0, 0, w, h);
      data = ctx.getImageData(0, 0, w, h).data;
    } catch {
      return; // frame not ready or cross-origin taint — skip
    }
    const lums = [];
    for (let i = 0; i < data.length; i += 4) {
      lums.push((0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255);
    }
    lums.sort((a, b) => a - b);
    const median = lums[Math.floor(lums.length / 2)] || 0;
    if (median < 0.01) return; // unpainted/black frame at load — wait for a real one
    const stops = autoExposureStops(median, 'log3g10', 0.34);
    setAutoExp(stops);
    if (!expTouchedRef.current) setExposure(stops);
  };

  // Selectable color/light looks, applied live in the preview and baked into the
  // saved still. RED footage decodes flat (Log3G10/Wide Gamut) so it looks weird
  // raw — default it to the IPP2 natural baseline. The list also surfaces a clip-
  // editor's live look and the clip's saved grade when present, then the presets.
  const editorGrade = gradeOverride !== undefined && gradeOverride && gradeSignature(gradeOverride) !== IDENTITY
    ? gradeOverride
    : null;
  const savedGrade = renderSource?.grade && gradeSignature(renderSource.grade) !== IDENTITY ? renderSource.grade : null;
  const lookOptions = useMemo(() => {
    const opts = [];
    if (editorGrade) opts.push({ id: 'override', name: 'Look you’re editing', grade: editorGrade });
    if (savedGrade) opts.push({ id: 'saved', name: 'Saved clip look', grade: savedGrade });
    PRESETS.forEach((p) => {
      if (p.kind === 'video' && !isRed) return; // RED-specific de-flatten preset
      opts.push({ id: p.id, name: p.name, grade: p.grade });
    });
    return opts;
  }, [editorGrade, savedGrade, isRed]);

  const [lookId, setLookId] = useState(() => {
    if (gradeOverride !== undefined && gradeOverride && gradeSignature(gradeOverride) !== IDENTITY) return 'override';
    if (isRed) return 'red-ipp2';
    return 'none';
  });
  const selectedOpt = lookOptions.find((o) => o.id === lookId) || lookOptions.find((o) => o.id === 'none');
  // On RED footage a display-referred preset (Tropical Water, Teal & Orange…)
  // must stack on top of the Log3G10/Wide-Gamut de-log, or it bakes a creative
  // look onto a flat log image (washed out). applyLook carries the RED IPP2
  // decode onto any such preset; RED-native looks pass through unchanged.
  const selectedGrade = (() => {
    if (!selectedOpt) return null;
    if (!isRed) return selectedOpt.grade;
    const composed = applyLook(RED_DEFAULT_GRADE, selectedOpt.grade);
    // Stills are judged on an sRGB display and handed to Lightroom, so encode the
    // de-log for sRGB (not Rec.709) — this makes the in-app preview match how a
    // normal JPG renders. Video clips keep Rec.709. Exposure normalizes this
    // clip's log level so the IPP2 baseline isn't crushed (or blown).
    return { ...composed, display: { ...(composed.display || {}), output: 'srgb', exposure } };
  })();
  const hasLook = !!selectedGrade && gradeSignature(selectedGrade) !== IDENTITY;
  const [glError, setGlError] = useState(false);
  const showGraded = hasLook && !glError;

  // Reframe controls. The crop is a normalized {x,y,w,h} rect the user drags by
  // its corners. `orig` = no crop (whole frame); `free` = any shape; a ratio
  // preset locks the box to that aspect while resizing.
  const [aspectId, setAspectId] = useState('orig');
  const [cropRect, setCropRect] = useState(null);
  const aspect = ASPECTS.find((a) => a.id === aspectId) || ASPECTS[0];
  const lockRatio = aspect.r && srcAR ? aspect.r / srcAR : null;

  // Build the starting rect for an aspect once the frame's AR is known. Ratio
  // presets get an 80%, centered box of the right shape; free keeps the prior
  // shape (or a default); original has no box.
  const rectForAspect = (id, ar, prev) => {
    if (id === 'orig') return null;
    if (id === 'free') return prev && prev.w ? prev : { x: 0.1, y: 0.1, w: 0.8, h: 0.8 };
    const a = ASPECTS.find((x) => x.id === id);
    return rectFor(a?.r, 0.8, { x: 0.5, y: 0.5 }, ar) || null;
  };

  const chooseAspect = (id) => {
    setAspectId(id);
    setCropRect((prev) => rectForAspect(id, srcAR, prev));
  };

  // When the frame AR first resolves, materialize the box for the current aspect.
  useEffect(() => {
    if (!srcAR) return;
    setCropRect((prev) => (aspectId === 'orig' ? null : (prev && prev.w ? prev : rectForAspect(aspectId, srcAR, prev))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcAR]);

  const crop = aspectId === 'orig' ? null : cropRect;

  const proxyReady = asset?.proxy?.status === 'ready' && !!asset?.proxyHlsUrl;
  const proxyKey = proxyDoc?.s3Key || '';

  // Resolve the *preview* source — always the fastest-to-scrub option available,
  // independent of what we ultimately render. Order: RED HLS proxy → this card
  // if it's already a proxy → master's in-place HLS proxy → separate proxy
  // asset's file → the original master (only when no proxy exists).
  useEffect(() => {
    let on = true;
    setUrl('');
    setError(null);
    const done = (u) => on && setUrl(u);
    const fail = (e) => on && setError(e.message || String(e));
    if (isRed) {
      if (proxyReady) setUrl(httpsify(asset.proxyHlsUrl));
      else setError('This RED clip is still decoding its preview proxy — try again once it’s ready.');
    } else if (asset?.isProxy && asset?.s3Key) {
      getAssetPlaybackUrl(asset.s3Key).then(done).catch(fail);
    } else if (asset?.proxyHlsUrl) {
      setUrl(httpsify(asset.proxyHlsUrl));
    } else if (proxyKey) {
      getAssetPlaybackUrl(proxyKey).then(done).catch(fail);
    } else if (asset?.s3Key) {
      getAssetPlaybackUrl(asset.s3Key).then(done).catch(fail);
    }
    return () => { on = false; };
  }, [asset, isRed, proxyReady, proxyKey]);

  // Attach hls.js for the proxy stream; native src for MP4.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !url) return undefined;
    const isHls = isRed || url.includes('.m3u8');
    if (isHls && Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true });
      hls.loadSource(url);
      hls.attachMedia(v);
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data?.fatal) setError('Could not load this clip for grabbing.');
      });
      return () => hls.destroy();
    }
    v.src = url;
    return undefined;
  }, [url, isRed]);

  const onMeta = (e) => {
    const v = e.target;
    setDur(v.duration || 0);
    if (v.videoWidth && v.videoHeight) setSrcAR(v.videoWidth / v.videoHeight);
    if (startTime > 0 && Number.isFinite(v.duration)) {
      v.currentTime = clamp(startTime, 0, v.duration);
    }
  };

  const step = (delta) => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = clamp((v.currentTime || 0) + delta, 0, dur || v.duration || 0);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  const onSeek = (e) => {
    const v = videoRef.current;
    if (!v) return;
    const val = Number(e.target.value);
    v.currentTime = clamp(val, 0, dur || v.duration || 0);
    setT(val);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      // Bake the chosen look into the still (or save clean when "None"). RED
      // grabs keep the look non-destructively on the asset; others bake it in.
      const assetId = await createStillGrabAsset(tripId, {
        source: renderSource || asset,
        timeSec: t,
        frameIndex: isRed && exactFrame !== '' ? Number(exactFrame) : null,
        format,
        crop: crop || null,
        applyGrade: hasLook,
        grade: hasLook ? selectedGrade : null,
        startingLook: hasLook ? { id: selectedOpt?.id || lookId, name: selectedOpt?.name || '' } : null,
        redSource: isRed,
        name,
        tags,
        movieId,
        movieTitle,
      });
      setSavedId(assetId);
      if (onSaved) onSaved(assetId);
      else setSaved(true);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Frame grab — ${asset?.name || ''}`} onClose={onClose} maxWidth={920}>
      {saved ? (
        <div style={{ padding: '8px 2px' }}>
          <div className="cs-empty" style={{ borderColor: 'var(--surf)' }}>
            ✓ Added to this trip’s media — your full-resolution still is rendering now
            {isRed ? ' (~1–2m: decoding the RED original at full sensor resolution)' : ' (~20–40s)'}.
            It’ll appear as a photo you can open, download, or drop into the magazine.
            <div style={{ marginTop: 6 }}>
              Saved as a non-destructive {isRed ? '16-bit RED ' : ''}negative — the look{crop ? ' and crop' : ''} stay
              fully editable. Open <b>Edit color &amp; light</b> to re-grade, re-expose, re-crop or reframe,
              then export JPEG / TIFF / WebP{isRed ? ', or a flat 16-bit TIFF for Lightroom' : ''}.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => { setSaved(false); setSavedId(null); setName(''); setTags(''); }}>Grab another</button>
            {savedId && (
              <button
                className="cs-btn cs-btn-primary cs-btn-sm"
                onClick={() => navigate(`/photo/${tripId}/${savedId}`)}
                title="Open this still in the photo editor to adjust color & light"
              >
                🎨 Edit color &amp; light
              </button>
            )}
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onClose}>Done</button>
          </div>
        </div>
      ) : (
        <>
          <div ref={stageRef} className="cs-grab-stage">
            {url ? (
              <video
                ref={(el) => { videoRef.current = el; setVideoEl(el); }}
                crossOrigin="anonymous"
                playsInline
                muted
                className="cs-grab-video"
                style={showGraded ? { opacity: 0 } : undefined}
                onLoadedMetadata={(e) => { onMeta(e); measureExposure(); }}
                onSeeked={measureExposure}
                onTimeUpdate={(e) => setT(e.target.currentTime || 0)}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onClick={togglePlay}
              />
            ) : (
              <div className="cs-empty">{error || 'Loading clip…'}</div>
            )}
            {url && showGraded && videoEl && (
              <GradedCanvas
                source={videoEl}
                grade={selectedGrade}
                onError={() => setGlError(true)}
                className="cs-grab-graded"
              />
            )}
            {url && crop && (
              <CropBox rect={crop} lockRatio={lockRatio} onChange={setCropRect} />
            )}
          </div>

          <div className="cs-grab-controls">
            <div className="cs-grab-row cs-grab-transport">
              <button className="cs-btn cs-btn-ghost cs-btn-sm cs-grab-play" onClick={togglePlay} disabled={!url} title={playing ? 'Pause' : 'Play'}>
                {playing ? '❚❚' : '►'}
              </button>
              <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => step(-1 / fps)} disabled={!url} title="Previous frame">⟨ frame</button>
              <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => step(1 / fps)} disabled={!url} title="Next frame">frame ⟩</button>
              <span className="cs-grab-frame">
                <strong>f{frameNo}</strong>{totalFrames ? ` / ${totalFrames}` : ''}
                <span className="cs-grab-tc">{fmtTime(t)} / {fmtTime(dur)}</span>
              </span>
            </div>
            <div className="cs-grab-row cs-grab-seekrow">
              <input
                className="cs-slider cs-grab-seek"
                type="range"
                min="0"
                max={dur || 0}
                step={1 / fps}
                value={Math.min(t, dur || 0)}
                onChange={onSeek}
                disabled={!url}
                aria-label="Scrub clip"
              />
            </div>

            {isRed && (
              <div className="cs-grab-row">
                <span className="cs-grab-label">Exact frame</span>
                <input
                  className="cs-input cs-input-sm"
                  type="number"
                  min="0"
                  placeholder="(native frame #)"
                  value={exactFrame}
                  onChange={(e) => setExactFrame(e.target.value)}
                  style={{ width: 130 }}
                  title="Grab a specific RED native frame index (overrides the scrubbed time). Leave blank to use the playhead."
                />
                <span className="cs-help" style={{ fontSize: 12 }}>
                  Overrides the playhead — targets the original RED frame numbering.
                </span>
              </div>
            )}

            <div className="cs-grab-row">
              <span className="cs-grab-label">Crop</span>
              {ASPECTS.map((a) => (
                <button
                  key={a.id}
                  className={`cs-btn cs-btn-sm ${aspectId === a.id ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
                  onClick={() => chooseAspect(a.id)}
                  title={a.id === 'orig' ? 'Keep the whole frame' : a.id === 'free' ? 'Crop to any shape — drag the corners' : `Lock the crop to ${a.label}`}
                >
                  {a.label}
                </button>
              ))}
            </div>

            {aspectId !== 'orig' && (
              <div className="cs-grab-row">
                <span className="cs-grab-label" />
                <span className="cs-help" style={{ fontSize: 12 }}>
                  {aspectId === 'free'
                    ? 'Drag a corner or edge to resize, drag inside to move.'
                    : `Locked to ${aspect.label} — drag a corner to resize, drag inside to move.`}
                </span>
                <button
                  type="button"
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  onClick={() => setCropRect(rectForAspect(aspectId, srcAR, null))}
                  title="Reset the crop box"
                >
                  Reset crop
                </button>
              </div>
            )}

            <div className="cs-grab-row">
              <span className="cs-grab-label">Look</span>
              <select
                className="cs-input cs-input-sm cs-grab-looksel"
                value={lookId}
                onChange={(e) => setLookId(e.target.value)}
                title="Apply a color & light preset to the grab. Fine-tune later in the photo editor."
              >
                {lookOptions.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
              {showGraded && <span className="cs-grab-livetag">● live preview</span>}
              {glError && hasLook && (
                <span className="cs-help" style={{ fontSize: 12 }}>Live preview off — the look still bakes into the saved still.</span>
              )}
            </div>

            {isRed && hasLook && (
              <div className="cs-grab-row">
                <span className="cs-grab-label">Exposure</span>
                <input
                  className="cs-slider"
                  type="range"
                  min="-3"
                  max="3"
                  step="0.05"
                  value={exposure}
                  onChange={(e) => { setExpTouched(true); expTouchedRef.current = true; setExposure(Number(e.target.value)); }}
                  style={{ flex: 1, maxWidth: 240 }}
                  title="Scene-linear exposure of the RED de-log (auto-normalized per clip; drag to taste)"
                />
                <span className="cs-help" style={{ fontSize: 12, minWidth: 64 }}>
                  {exposure > 0 ? '+' : ''}{exposure.toFixed(2)} EV
                </span>
                <button
                  type="button"
                  className={`cs-btn cs-btn-sm ${expTouched ? 'cs-btn-ghost' : 'cs-btn-primary'}`}
                  title={`Auto-normalize this clip's exposure (${autoExp > 0 ? '+' : ''}${autoExp.toFixed(2)} EV)`}
                  onClick={() => { setExpTouched(false); expTouchedRef.current = false; setExposure(autoExp); }}
                >
                  {expTouched ? 'Auto' : '● Auto'}
                </button>
              </div>
            )}

            <div className="cs-grab-row cs-grab-naming">
              <label className="cs-grab-field">
                <span className="cs-grab-label">Name <em>(optional)</em></span>
                <input
                  className="cs-input cs-input-sm"
                  placeholder="e.g. Backlit barrel"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className="cs-grab-field">
                <span className="cs-grab-label">Tags <em>(comma-separated)</em></span>
                <input
                  className="cs-input cs-input-sm"
                  placeholder="e.g. cover, barrel, golden-hour"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                />
              </label>
            </div>

            <div className="cs-grab-row">
              <span
                className="cs-help"
                style={{ fontSize: 12 }}
                title="Saved as a non-destructive PNG digital negative. The look AND the crop are kept on the photo (never baked into the master), so you can re-grade, re-expose, re-crop and reframe with full latitude in the photo editor, then export JPEG / TIFF / WebP. RED keeps full 16-bit latitude."
              >
                {isRed ? '16-bit RED negative' : 'PNG negative'} · non-destructive — editable look + crop
              </span>
              <span style={{ flex: 1 }} />
              <button
                className="cs-btn cs-btn-primary cs-btn-sm"
                disabled={busy || !url}
                onClick={save}
                title={`Save this frame as a non-destructive still. The full-resolution${isRed ? ' RED' : ''} render finishes in the background (~${isRed ? '1–2m' : '20–40s'}).`}
              >
                {busy ? 'Saving…' : '🖼 Save to trip photos'}
              </button>
            </div>

            <div className="cs-help" style={{ fontSize: 12 }}>
              Grabs the frame from the <strong>original max-resolution</strong> source
              {isRed ? ' (RED .R3D, full sensor)' : ''}
              {previewingProxy ? ' — you’re scrubbing the fast 1080p proxy, but it grabs the full-quality master (rendering may take a little longer)' : ''}
              {hasLook ? `, with the “${selectedOpt?.name}” look kept non-destructively` : ''}
              {crop ? ', with an editable crop' : ''}. The still is saved into this trip’s media as a photo.
            </div>

            {error && <div className="cs-empty" style={{ marginTop: 4 }}>{error}</div>}
          </div>
        </>
      )}
    </Modal>
  );
}
